import type { DeckConfig, GameState } from "@fiveout/core";
import {
  aValue,
  buildStacks,
  card,
  draws,
  nextCard,
  showing,
  TUTORIAL_PLAYER_ID,
  TUTORIAL_PLAYER_NAME,
  tutorialPlayer,
  tutorialRound,
  type Tutorial,
  type TutorialStep,
} from "./script";

/**
 * Tutorial – Wild Cards: Lightning, Swap and Peek, and a wild turning up in the
 * middle of a +1 run. One player, no house rules, every wild on — the table a
 * new game deals.
 *
 * Indices are 0 center, 1 up, 2 down, 3 left, 4 right; piles are top-first.
 * Every pile starts two deep so Lightning has something to uncover, and the
 * left pile is the one it lands on, so by the last step it is deep enough for
 * the Smash to be worth watching.
 */
const DECK_CONFIG: DeckConfig = { size: 0.5, wilds: { smash: true, lightning: true, swap: true, peek: true } };

const BOARD: number[][] = [
  [5, 10], // 0 center — the Swap lands here
  [8, 3], // 1 up
  [12, 2], // 2 down
  [6, 9], // 3 left — Lightning, then the run that ends in a Smash
  [13, 4], // 4 right — the Peek
];

const FILLER = [8, 2, 10, 5, 13, 1, 7, 11, 3, 9, 6, 12, 4, 10, 2, 8, 5, 1, 13, 6];

/**
 * The card the Peek turns face up. Primed by the Peek's step, where it is left
 * on top of the deck, and primed again (as itself) by the step that plays it.
 */
const PEEKED = card("twc-peeked", 12, "diamonds");

function initialState(): GameState {
  return tutorialRound({
    id: "tutorial-wild-cards",
    deckConfig: DECK_CONFIG,
    stacks: buildStacks("twc", BOARD),
    players: [tutorialPlayer(TUTORIAL_PLAYER_ID, TUTORIAL_PLAYER_NAME, draws("twc-deck", FILLER))],
  });
}

const STEPS: TutorialStep[] = [
  {
    id: "welcome",
    title: "Wild cards",
    prompt:
      "**Wild cards** have no number, so they're never a wrong guess. A new game shuffles four kinds into the deck.\n\n" +
      "**Smash** is in the Basics tutorial. Play a few turns to meet the other three.",
    cta: "Deal me in",
  },
  {
    id: "lightning",
    title: "Your turn",
    prompt: "Make **any guess** you like on the **left** pile.",
    plays: [
      {
        move: { kind: "guess", stackIndex: 3, guess: "higher", anyGuess: true },
        draws: draws("twc-lightning", ["lightning", 11]),
        pauses: [
          { on: "wild-played", at: "showcase", title: "Lightning", text: "**You drew a wild card: Lightning!**" },
          { on: "lightning-strike", title: "Lightning", text: "**Lightning** knocked the top card off each of the other four piles, out of the game." },
          { on: "wild-follow-up-card", title: "A free card", text: "As after every wild, your next card went on top of it, free." },
        ],
      },
    ],
  },
  {
    id: "swap",
    title: "Your turn",
    prompt: "Your turn again. Make **any guess** on the **middle** pile.",
    plays: [
      {
        move: { kind: "guess", stackIndex: 0, guess: "lower", anyGuess: true },
        draws: draws("twc-swap", ["swap", 7]),
        pauses: [
          {
            on: "wild-played",
            at: "showcase",
            title: "Swap",
            text: "**You drew Swap!** It lets you trade the top cards of two piles, once your turn is over.",
          },
          { on: "wild-follow-up-card", title: "Swap", text: "Your next card covered it, free. Now your turn is over, so it's time to swap." },
        ],
      },
    ],
  },
  {
    id: "swap-choice",
    title: "Pick two piles",
    prompt:
      "Pick any two piles to trade their top cards. Only the top cards move; the rest of each pile stays put.\n\n" +
      "Tap the **middle** pile, then the **top** pile.",
    plays: [{ move: { kind: "swap", stackIndices: [0, 1] } }],
    debrief:
      "The two top cards traded places. Use it to leave yourself an easy guess, or a hard one for the next player.",
    cta: "Next",
  },
  {
    id: "peek",
    title: "Your turn",
    prompt: "Make **any guess** on the **right** pile.",
    plays: [
      {
        move: { kind: "guess", stackIndex: 4, guess: "higher", anyGuess: true },
        draws: [...draws("twc-peek", ["peek", 9]), PEEKED],
        pauses: [{ on: "wild-played", at: "showcase", title: "Peek", text: "**You drew Peek!** It shows you your next card before you play it." }],
      },
    ],
    debriefTitle: "Peek",
    debrief: (s) =>
      `**Peek** turned your next card face up on your deck: ${aValue(nextCard(s))}. It stays up, for everyone to see, until you play it.`,
    cta: "Next",
  },
  {
    id: "wild-in-a-run",
    title: "A wild in a run",
    prompt: (s) =>
      `You know your next card is ${aValue(nextCard(s))}, and the **left** pile shows ${aValue(showing(s, 3))}. Tap ▲ to guess Higher.`,
    plays: [
      {
        move: { kind: "guess", stackIndex: 3, guess: "higher" },
        draws: [PEEKED, ...draws("twc-run", ["smash", 6])],
        pauses: [
          { on: "card-played", at: "reveal", text: "**A 12**: exactly 1 higher than 11, so you earn a **bonus card** on the same pile." },
          {
            on: "wild-played",
            at: "showcase",
            title: "A wild bonus card",
            text: "**Your bonus card is a Smash!** A wild goes off however it's played, even as a bonus card.",
          },
          { on: "wild-annihilation", title: "Smash", text: "**Smash** cleared the pile, out of the game." },
          { on: "wild-follow-up-card", title: "A free card", text: "Then your next card went on top, free, as after every wild." },
        ],
      },
    ],
  },
  {
    id: "done",
    title: "That's every wild",
    prompt:
      "**Lightning**, **Swap**, **Peek** and **Smash**. A new game shuffles a few of them into the deck.\n\n" +
      "Next, **House Rules** shows the extra rules every new game starts with.",
    cta: "Play Five Out",
  },
];

export const WILD_CARDS: Tutorial = {
  id: "wild-cards",
  title: "Tutorial – Wild Cards",
  initialState,
  steps: STEPS,
};
